import gql from "graphql-tag";
import type {
    AuthSettings,
    CloudAccessType,
    CompanyAccessOption,
    CompanySettings,
    CompanyTOSState,
    CompanyType,
    CustomerInfo,
    OperatingSchedule,
    ResellerInfo,
    User,
    WorkbenchAccount,
} from "../types";
import { Role } from "../types";
import { CompanyFragments } from "../Fragments/Company";
import { useLazyQuery } from "@apollo/client";
import { useContext } from "react";
import { UserContext } from "../../components/User/UserProvider";
import { Notifier } from "../../view/Notifier";
import { useQueryWithCatch } from "../../utils/hooks/useQueryWithCatch";

export const GET_COMPANY_AS_ADMIN = gql`
    query company($id: Int) {
        company(id: $id) {
            id
            tosState
            internal
            companyType
            cloudAccessType
            ...adminEditableFields
            customerInfo {
                accountNumber
                name
                phoneNumber
                email
            }
            eligibleForSSO
            ...operatingSchedule
            ...authSettings
        }
    }
    ${CompanyFragments.adminEditableFields}
    ${CompanyFragments.operatingSchedule}
    ${CompanyFragments.authSettings}
`;

// Read-only variant for Role.User: omits authSettings fields (providerDetails, attributeMappings)
// that GraphQL Shield restricts to admins, since requesting them would fail the whole query.
export const GET_COMPANY_AS_USER = gql`
    query company($id: Int) {
        company(id: $id) {
            id
            tosState
            internal
            companyType
            cloudAccessType
            ...adminEditableFields
            customerInfo {
                accountNumber
                name
                phoneNumber
                email
            }
            eligibleForSSO
            ...operatingSchedule
            ...authSettingsReadOnly
        }
    }
    ${CompanyFragments.adminEditableFields}
    ${CompanyFragments.operatingSchedule}
    ${CompanyFragments.authSettingsReadOnly}
`;

export const GET_COMPANY_APPS = gql`
    query company($id: Int) {
        company(id: $id) {
            id
            applications {
                id
                name
                visible
                dateDeleted
                applicationType {
                    id
                    description
                    roles {
                        id
                        name
                    }
                }
            }
        }
    }
`;

export interface CompanyAsAdmin {
    id: number;
    name: string;
    internal: boolean;
    tosState?: CompanyTOSState;
    companyType?: CompanyType;
    cloudAccessType?: CloudAccessType;
    analyzeServerAddress?: string;
    analyzeDeploymentType?: string;
    admins: User[];
    settings: CompanySettings;
    customerInfo?: CustomerInfo;
    operatingSchedule?: OperatingSchedule;
    authSettings?: AuthSettings;
    eligibleForSSO?: boolean;
}

export interface GetCompanyQueryResult {
    company: CompanyAsAdmin;
}

export function useCompanyAsAdminQuery() {
    const { companyId, role } = useContext(UserContext);
    // Role.User only has read-only access, so use the query that omits the admin-only
    // authSettings fields GraphQL Shield would otherwise reject for that role.
    const query = role === Role.User ? GET_COMPANY_AS_USER : GET_COMPANY_AS_ADMIN;
    const [fetch, { data, loading, error, refetch }] = useLazyQuery<GetCompanyQueryResult>(query);
    if (error) {
        Notifier.error(error);
    }
    if (!companyId) {
        // This enables us to call the hook unconditionally in Components shared by Global Admins,
        // which only display company info for Company Admins. EG: <TopNavbarWrapper>
        return {
            companyLoading: false,
        };
    }

    if (!data && !loading && !error) {
        void fetch();
    }

    return {
        companyLoading: loading,
        company: data?.company,
        companyRefetch: refetch,
    };
}

export const GET_COMPANY_GLOBAL_ADMIN = gql`
    query company($id: Int!) {
        company(id: $id) {
            id
            internal
            companyType
            cloudAccessType
            ...adminEditableFields
            customerInfo {
                accountNumber
                name
                phoneNumber
                email
            }
            resellerInfo {
                name
                phoneNumber
                email
                contactPerson
                accountNumber
            }
            accessOptions {
                id
                url
                state
                groups {
                    id
                    name
                }
                companyWithSameUrl {
                    id
                    name
                }
            }
            workbenchAccounts {
                id
                name
            }
            ...operatingSchedule
        }
    }
    ${CompanyFragments.adminEditableFields}
    ${CompanyFragments.operatingSchedule}
`;
export interface CompanyAsGlobalAdmin extends CompanyAsAdmin {
    internal: boolean;
    companyType?: CompanyType;
    cloudAccessType?: CloudAccessType;
    resellerInfo?: ResellerInfo;
    accessOptions: CompanyAccessOption[];
    workbenchAccounts: WorkbenchAccount[];
}

export interface GetCompanyQueryAsGlobalAdminVariables {
    id: number;
}
export interface GetCompanyQueryAsGLobalAdminResult {
    company: CompanyAsGlobalAdmin;
}

export function useCompanyAsGlobalAdminQuery(id: number) {
    const { data, loading, refetch } = useQueryWithCatch<
        GetCompanyQueryAsGLobalAdminResult,
        GetCompanyQueryAsGlobalAdminVariables
    >(GET_COMPANY_GLOBAL_ADMIN, { variables: { id } });

    return {
        companyLoading: loading,
        company: data?.company,
        companyRefetch: refetch,
    };
}
